import {
  DateType,
  InwardResponse,
  OutwardResponse,
  SearchExtension,
  SearchService,
  Solution,
} from "@gen/travel/v1/search_pb";
import { TravelService } from "@gen/travel/v1/travel_pb";
import { client } from "./config";
import { Place } from "@gen/place/v1/place_pb";
import { CabinClass } from "@pages/booking/search/Search";
import { timestampFromDate } from "@bufbuild/protobuf/wkt";
import { CatalogService } from "@gen/travel/v1/catalog_pb";
import {
  Journey,
  PlaneCabinClass,
  Product,
  Ticket,
  TrainCabinClass,
  Transportation,
  Travel,
  Passenger as PassengerType,
  Price,
  DisabilityCard,
  Status,
  Gender,
} from "@gen/travel/v1/common_pb";
import {
  DAP,
  PlaneCabinClass as DAPPlaneCabinClass,
  TrainCabinClass as DAPTrainCabinClass,
} from "@gen/dap/v1/dap_pb";
import { PageRequest, PageResponse } from "@gen/tsam/v1/page_pb";
import { ExchangeSolution } from "@gen/travel/v1/exchange_pb";

export interface Passenger {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  birthDate: string;
  cards: Card[];
  disability?: Disability;
  hasCompanionFare?: DisabilityCard;
  gender: Gender;
  corsicaResidentNumber?: string;
}

export interface Card {
  type: string;
  number: string;
  effectiveDate: string;
  expirationDate: string;
}

export interface Disability {
  type: string;
  wheelchair: Wheelchair;
  needsCompanion?: boolean;
}

export interface Wheelchair {
  type: string;
  dimensions: Dimensions;
  weight: number;
  wheelThickness: number;
}

export interface Dimensions {
  width: number;
  height: number;
  length: number;
}

export interface SearchDate {
  date: Date;
  type: "departure" | "arrival";
}

export const DATE_TYPE_OPTIONS: {
  labelKey: string;
  value: SearchDate["type"];
}[] = [
  { labelKey: "search.leaveAt", value: "departure" },
  { labelKey: "search.arriveAt", value: "arrival" },
];

export interface SearchRequest {
  dapNumber: string;
  origin?: Place;
  destination?: Place;
  outwardDate?: SearchDate;
  inwardDate?: SearchDate;
  mainPassenger?: Passenger;
  additionalPassengers?: Passenger[];
  transportations?: Transportation[];
  outwardOfferId?: string;
  bypassOverlapping?: boolean;
  travelId?: string;
  extension?: SearchExtension;
}

export type SearchResponse = OutwardResponse | InwardResponse;

type Named = { firstName: string; lastName: string };

export function sameName(a: Named, b: Named) {
  return a.firstName === b.firstName && a.lastName === b.lastName;
}

// companions are matched by name, the main passenger is the traveler left over, like in addToCart.
// Matched travelers are consumed so homonyms each get their own id.
export function withTravelerIds(
  req: SearchRequest,
  travelers: (Named & { id: string })[],
): SearchRequest {
  const remaining = [...travelers];
  const take = (match: (t: Named) => boolean) => {
    const i = remaining.findIndex(match);
    return i < 0 ? "" : remaining.splice(i, 1)[0].id;
  };
  const additionalPassengers = req.additionalPassengers?.map((p) => ({
    ...p,
    id: take((t) => sameName(t, p)),
  }));
  return {
    ...req,
    additionalPassengers,
    mainPassenger: req.mainPassenger && {
      ...req.mainPassenger,
      id: take(() => true),
    },
  };
}

// the store always keeps the outward request, the inward one is derived from it
export function toInwardRequest(
  outward: SearchRequest,
  dap: DAP,
  outwardOfferId: string,
  travelId?: string,
): SearchRequest {
  return {
    ...outward,
    origin: dap.destination,
    destination: dap.origin,
    outwardOfferId,
    travelId,
  };
}

export async function searchJourney(req: SearchRequest) {
  if (!req.inwardDate) {
    throw new Error("inward date cannot be null");
  }
  const baseParams = {
    dapNumber: req.dapNumber,
    origin: req.origin,
    mainPassenger: req.mainPassenger,
    additionalPassengers: req.additionalPassengers,
    transportations: req.transportations,
    travelId: req.travelId,
    extension: req.extension,
  };

  if (req.outwardOfferId) {
    return await client(SearchService).inward({
      ...baseParams,
      outwardOfferId: req.outwardOfferId,
      inwardDate: mapSearchDate(req.inwardDate),
      ...(req.destination && {
        destination: req.destination,
      }),
    });
  }

  if (!req.outwardDate) {
    throw new Error("outward date cannot be null");
  }

  return await client(SearchService).outward({
    ...baseParams,
    outwardDate: mapSearchDate(req.outwardDate),
    inwardDate: mapSearchDate(req.inwardDate),
    bypassOverlapping: req.bypassOverlapping || false,
  });
}

function mapSearchDate(s: SearchDate) {
  return {
    date: timestampFromDate(s.date),
    type: s.type == "arrival" ? DateType.ARRIVAL : DateType.DEPARTURE,
  };
}

export async function getMoreOffers(
  dap: DAP,
  solution: Solution | ExchangeSolution,
) {
  if (!solution.more) {
    throw new Error("cannot search more offers for this solution");
  }
  return await client(SearchService).more({
    dapNumber: dap.number,
    link: solution.more,
  });
}

export async function getCardTypes(dapNumber: string) {
  return await client(CatalogService).listCards({
    dapNumber: dapNumber,
  });
}

export async function getDisabilityTypes(dapNumber: string) {
  return await client(CatalogService).listDisabilities({
    dapNumber: dapNumber,
  });
}

export async function getWheelchairTypes(dapNumber: string) {
  return await client(CatalogService).listWheelchairs({
    dapNumber: dapNumber,
  });
}

export function isTrain(solution: Solution | ExchangeSolution): boolean {
  return (
    solution.journey?.segments.some(
      (s) => s.transport?.type == Transportation.TRAIN,
    ) || false
  );
}

export function availableCabinClasses(
  dap: DAP,
  solution: Solution | ExchangeSolution,
): CabinClass[] {
  const dapTrainCabinClass = dap.trainCabinClass || DAPTrainCabinClass.SECOND;
  const dapPlaneCabinClass = dap.planeCabinClass || DAPPlaneCabinClass.ECONOMY;
  if (isTrain(solution)) {
    if (
      dapTrainCabinClass &&
      dapTrainCabinClass === DAPTrainCabinClass.SECOND
    ) {
      return [TrainCabinClass.SECOND];
    }
    return [TrainCabinClass.SECOND, TrainCabinClass.FIRST];
  } else {
    if (
      dapPlaneCabinClass &&
      dapPlaneCabinClass === DAPPlaneCabinClass.ECONOMY
    ) {
      return [PlaneCabinClass.ECONOMY];
    } else if (
      dapPlaneCabinClass &&
      dapPlaneCabinClass === DAPPlaneCabinClass.ECONOMY_PREMIUM
    ) {
      return [PlaneCabinClass.ECONOMY, PlaneCabinClass.ECONOMY_PREMIUM];
    }

    return [
      PlaneCabinClass.ECONOMY,
      PlaneCabinClass.ECONOMY_PREMIUM,
      PlaneCabinClass.BUSINESS,
    ];
  }
}

export function availableCabinClassesWithLabels(
  solution: Solution | ExchangeSolution,
  t: (s: string) => string,
): { cabinClass: CabinClass; label: string }[] {
  if (isTrain(solution)) {
    return [
      {
        cabinClass: TrainCabinClass.SECOND,
        label: t("results.secondClass"),
      },
      {
        cabinClass: TrainCabinClass.FIRST,
        label: t("results.firstClass"),
      },
    ];
  } else {
    return [
      {
        cabinClass: PlaneCabinClass.ECONOMY,
        label: t("results.economy"),
      },
      {
        cabinClass: PlaneCabinClass.ECONOMY_PREMIUM,
        label: t("results.economyPremium"),
      },
      {
        cabinClass: PlaneCabinClass.BUSINESS,
        label: t("results.business"),
      },
    ];
  }
}

export function priceOfTravel(travel: Travel): Price {
  if (!travel.initialPrices[0].price) throw new Error("No initial prices");

  return travel.initialPrices
    .map((p) => p.price)
    .filter((p) => !!p)
    .slice(1)
    .reduce<Price>((acc, val) => {
      acc.amount += val.amount;
      return acc;
    }, travel.initialPrices[0].price);
}

export function priceOfJourney(travel: Travel, journey: Journey): number {
  return travel.initialPrices.reduce((acc, val) => {
    const products = productsOfJourney(travel, journey);
    if (products.some((p) => val.productIds.includes(p.id))) {
      return acc + Number(val.price?.amount);
    }
    return acc;
  }, 0);
}

export function priceOfProduct(
  travel: Travel,
  product: Product,
): Price | undefined {
  return travel.initialPrices.find((p) => p.productIds.includes(product.id))
    ?.price;
}

export function productsOfJourney(travel: Travel, journey: Journey): Product[] {
  return travel.products.filter((p) =>
    journey.segments.some((s) => s.id === p.segmentId),
  );
}

export function productsByPassenger(travel: Travel, journey: Journey) {
  return travel.passengers.map((passenger) => ({
    passenger,
    products: productsOfJourney(travel, journey).filter((p) => {
      return p.passengerId === passenger.id;
    }),
  }));
}

export function journeyTicketsForPassenger(
  travel: Travel,
  journey: Journey,
  passenger: PassengerType,
): Ticket[] {
  const products = productsOfJourney(travel, journey).filter(
    (p) => p.passengerId === passenger.id,
  );
  return travel.tickets.filter((t) =>
    products.some((p) => t.productIds.includes(p.id)),
  );
}

export interface GetTravelsParams {
  page?: PageRequest;
  filters?: {
    from?: Date;
    to?: Date;
    place?: string;
    statuses?: Status[];
    user?: string;
    agencyId?: number;
    caisseId?: number;
  };
  signal?: AbortSignal;
}

export interface GetTravelsResponse {
  travels: Travel[];
  page?: PageResponse;
}

function localDateToTimestamp(date: Date) {
  // Create a new date at midnight UTC to avoid timezone conversion issues
  const utcDate = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0),
  );
  return timestampFromDate(utcDate);
}

export async function getTravels(
  params: GetTravelsParams,
): Promise<GetTravelsResponse> {
  const request = {
    page: params.page,
    filters: params.filters
      ? {
          from: params.filters.from
            ? localDateToTimestamp(params.filters.from)
            : undefined,
          to: params.filters.to
            ? localDateToTimestamp(params.filters.to)
            : undefined,
          place: params.filters.place,
          statuses: params.filters.statuses,
          user: params.filters.user,
          agencyId: params.filters.agencyId,
          caisseId: params.filters.caisseId,
        }
      : undefined,
  };

  return await client(TravelService).list(request, {
    signal: params.signal,
  });
}

export async function getTravel(id: string) {
  return await client(TravelService).get({ travelId: id });
}

export async function getPlacementChoices(
  travelId: string,
  dapNumber: string,
  offerIds: string[],
) {
  const request = {
    travelId,
    dapNumber,
    offerIds,
  };
  return await client(SearchService).placementChoice(request);
}
